import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { LinkProps } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button, ButtonProps } from 'src/components/Button';
import { Container, ContainerProps } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Icon, IconDefinition } from 'src/components/Icon';
import { IconButton, IconButtonProps } from 'src/components/IconButton';
import { PageHeader } from 'src/components/PageHeader';
import { SafeArea } from 'src/components/SafeArea';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import { useTheme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Grow } from 'src/components/transitions/Grow';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import { isTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import { useReloadChannel } from 'src/shared/useReloadChannel';

import { UserDropdown } from '../UserDropdown';
import { WebviewBackButton } from '../WebviewBackButton';

import { DownloadAppBanner } from './DownloadAppBanner';
import { HelloBar } from './HelloBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { MainNav } from './MainNav';
import { MainNavDreamcoat } from './MainNavDreamcoat';
import { TopBar } from './TopBar';
import { TopBarDreamcoat } from './TopBarDreamcoat';
import { DecoratedNavItem } from './navUtils';

export interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
  icon?: IconDefinition;
}

const DreamcoatMainNavWidth = '84px';
const LegacyMainNavWidth = '280px';

export interface AppFrameProps {
  children: React.ReactNode;
  drawerOpen: boolean;
  title: React.ReactNode;
  collapsedTitle?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  row?: React.ReactNode;

  customNavItems?: DecoratedNavItem[];

  /**
   * frameOnly: Skips Container wrapper and PageHeader but keeps all navigation chrome
   * - Navigation sidebar remains visible
   * - Top bar and banners remain visible
   * - Mobile header remains visible
   * - Only removes the page container and header wrapper around content
   * - Use for: Custom page layouts that need navigation but want full control over content layout
   */
  frameOnly?: boolean;

  compactTitle?: boolean;

  /**
   * noChrome: Removes main navigation chrome but keeps some page structure
   * - Removes navigation sidebar, top bar, and banners
   * - Keeps mobile header on small screens
   * - Keeps page header on desktop (when frameOnly is false)
   * - Keeps container wrapping
   * - Set via URL parameter: ?nochrome=true
   * - Use for: Minimal views, webview embedding
   */

  noChrome?: boolean;

  /**
   * fixedHeader: Makes the PageHeader sticky to the top of the page.
   * Doesnt affect mobile page header as thats sticky by default
   */
  fixedHeader?: boolean;

  containerMaxWidth?: ContainerProps['maxWidth'];
  backButtonTitle?: string;
  backButtonLinkTo?: LinkProps['to'] | (() => void);
  isAdmin?: boolean;
  bgcolor: string;

  /**
   * disabled: Completely disables the app frame - renders only children
   * - Removes ALL app frame elements (navigation, headers, containers, banners)
   * - Renders only the raw page content
   * - No navigation, no headers, no containers, no chrome of any kind
   * - Use for: True iframe embedding, completely isolated page rendering
   */
  disabled?: boolean;
}

export const AppFrame: React.FC<AppFrameProps> = ({
  drawerOpen,
  title,
  collapsedTitle,
  actions,
  row,
  frameOnly,
  fixedHeader,
  compactTitle,
  noChrome,
  backButtonTitle,
  backButtonLinkTo,
  bgcolor,
  customNavItems,
  containerMaxWidth = 'lg',
  isAdmin = false,
  disabled = false,
  children,
}) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const { user, sessionTrainer, session } = useOptionalSession();

  const dreamcoatFrame = session
    ? hasFeatureFlag(session, FeatureFlag.DreamcoatFrame)
    : false;

  const frameContentPaddingLeft = !smDown && !xlDown ? LegacyMainNavWidth : 0;

  // Subscribe to reload channel for admin-triggered page reloads
  useReloadChannel();

  const resetThemeColors = {
    color: 'text.primary',
    bgcolor: bgcolor || 'background.default',
    transition: 'background-color 0.5s',
    minHeight: '100svh',
  };

  // Wait for user and sessionTrainer to load before evaluating banner conditions
  const showDownloadBanner =
    !!user && // Check if user exists
    !!sessionTrainer && // Check if sessionTrainer exists
    !isAdmin &&
    !isTrainer(user) &&
    !user.hideMobileAppBanner &&
    !session?.hideAppCta;

  const [navToggled, setNavToggled] = useState(!xlDown);
  const { isWebview, isInIframe } = useNativeBridge();
  const navOpen = navToggled;

  // If disabled or in iframe, render only the children without any app frame elements
  if (disabled || isInIframe) {
    return <>{children}</>;
  }

  if (noChrome) {
    return (
      <Box sx={resetThemeColors}>
        {smDown && (
          <MobileAppHeader
            title={title}
            collapsedTitle={collapsedTitle}
            actions={actions}
            row={row}
            compactTitle={true}
            bgcolor={bgcolor}
          />
        )}
        <Box sx={{ width: '100%' }}>
          <DrawerLayout drawerOpen={drawerOpen}>
            {!!frameOnly && <>{children}</>}

            {!frameOnly && (
              <Container maxWidth={containerMaxWidth} sx={{ mx: 'auto' }}>
                {!smDown && (
                  <PageHeader
                    backButtonLinkTo={backButtonLinkTo}
                    title={title}
                    actions={actions}
                    row={row}
                    backButtonTitle={backButtonTitle}
                    fixed={fixedHeader}
                  />
                )}
                {children}
              </Container>
            )}
          </DrawerLayout>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={resetThemeColors}>
      {smDown && (
        <>
          {!isWebview && (
            <>
              <ImpersonationBanner />
              {showDownloadBanner ? <DownloadAppBanner /> : <HelloBar />}
            </>
          )}
          <MobileAppHeader
            title={title}
            collapsedTitle={collapsedTitle}
            actions={actions}
            row={row}
            onClickHamburger={() => setNavToggled(!navOpen)}
            compactTitle={compactTitle}
            bgcolor={bgcolor}
          />
        </>
      )}
      <Box
        sx={{
          paddingLeft:
            dreamcoatFrame && !smDown
              ? DreamcoatMainNavWidth
              : frameContentPaddingLeft,
          width: '100%',
          display: 'flex',
          transition: (theme) =>
            theme.transitions.create('padding', {
              easing: drawerOpen
                ? theme.transitions.easing.easeOut
                : theme.transitions.easing.sharp,
              duration: drawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        {dreamcoatFrame ? (
          <MainNavDreamcoat
            open={smDown && navToggled} // Dreamcoat nav is always open on desktop
            toggleOpen={() => xlDown && setNavToggled(!navOpen)}
            navWidth={DreamcoatMainNavWidth}
            isAdmin={isAdmin}
            customNavItems={customNavItems}
          />
        ) : (
          <MainNav
            open={navOpen}
            toggleOpen={() => xlDown && setNavToggled(!navOpen)}
            navWidth={LegacyMainNavWidth}
            isAdmin={isAdmin}
            customNavItems={customNavItems}
          />
        )}

        <Box sx={{ width: '100%' }}>
          {!smDown && (
            <>
              {dreamcoatFrame ? (
                <TopBarDreamcoat isAdmin={isAdmin} bgcolor={bgcolor} />
              ) : (
                <TopBar
                  toggleOpenNav={() => setNavToggled(!navOpen)}
                  hideNavToggle={dreamcoatFrame}
                  isAdmin={isAdmin}
                  bgcolor={bgcolor}
                ></TopBar>
              )}

              {!isWebview && (
                <>
                  <ImpersonationBanner />
                  {showDownloadBanner ? <DownloadAppBanner /> : <HelloBar />}
                </>
              )}
            </>
          )}
          <DrawerLayout drawerOpen={drawerOpen}>
            {!!frameOnly && <>{children}</>}

            {!frameOnly && (
              <Container
                maxWidth={containerMaxWidth}
                sx={{
                  mx: 'auto',
                  zIndex: 1,
                  bgcolor: resetThemeColors.bgcolor,
                }}
              >
                {!smDown && (
                  <PageHeader
                    backButtonLinkTo={backButtonLinkTo}
                    title={title}
                    actions={actions}
                    row={row}
                    backButtonTitle={backButtonTitle}
                    fixed={fixedHeader}
                  />
                )}
                {children}
              </Container>
            )}
          </DrawerLayout>
        </Box>
      </Box>
    </Box>
  );
};

export interface MobileAppHeaderProps {
  title: React.ReactNode;
  collapsedTitle?: React.ReactNode;
  onClickBack?: () => void;
  onClickHamburger?: () => void;
  navigation?: React.ReactNode;
  row?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  compactTitle?: boolean;
  bgcolor?: string;
}

export const MobileAppHeader: React.FC<MobileAppHeaderProps> = ({
  title,
  collapsedTitle,
  actions,
  row,
  onClickHamburger,
  navigation,
  compactTitle,
  bgcolor,
}) => {
  const { session } = useOptionalSession();

  const dreamcoatFrame = session
    ? hasFeatureFlag(session, FeatureFlag.DreamcoatFrame)
    : false;

  const theme = useTheme();
  const { isWebview, inset } = useNativeBridge();

  const { ref: bigTitleRef, inView: bigTitleInView } = useInView({
    threshold: 1,
  });

  return (
    <>
      <SafeArea
        top
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '55px',
          position: 'sticky',
          top: '0px',
          bgcolor: bgcolor || 'background.default',
          zIndex: theme.zIndex?.appBar,
          transition: 'background-color 0.5s',
          borderBottom: dreamcoatFrame ? '1px solid' : '0px',
          borderColor: theme.palette.divider,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ minWidth: '56px' }}
          p={1}
        >
          {!isWebview &&
            onClickHamburger &&
            (dreamcoatFrame ? (
              <IconButton
                variant="tertiary"
                onClick={onClickHamburger}
                sx={{ mr: 1 }}
              >
                <Icon size="small" color="action" icon={faBars} />
              </IconButton>
            ) : (
              <IconButton onClick={onClickHamburger} sx={{ mr: 1 }}>
                <Icon size="medium" color="action" icon={faBars} />
              </IconButton>
            ))}
          {isWebview && <WebviewBackButton />}
          {navigation && navigation}
          <Grow in={!bigTitleInView || compactTitle === true}>
            <Typography
              noWrap
              variant="h6"
              sx={{ ml: 1, textTransform: 'capitalize' }}
            >
              {collapsedTitle || title}
            </Typography>
          </Grow>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          p={1}
          sx={{
            flexShrink: 0,
            minWidth: '56px',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          }}
        >
          {dreamcoatFrame && <UserDropdown />}

          {Array.isArray(actions) &&
            actions.map((buttonProps, i) => {
              if (buttonProps.icon) {
                const { icon, label: _label, ...iconButtonProps } = buttonProps;

                return (
                  <IconButton key={i} {...(iconButtonProps as IconButtonProps)}>
                    <Icon color="action" size="medium" icon={icon} />
                  </IconButton>
                );
              }

              return (
                <Button key={i} {...buttonProps}>
                  {buttonProps.label}
                </Button>
              );
            })}
          {!Array.isArray(actions) && actions}
        </Stack>
      </SafeArea>
      {compactTitle !== true && (
        <Typography
          noWrap
          variant="h4"
          sx={{ mt: 2, mb: 3, px: 2, textTransform: 'capitalize' }}
          ref={bigTitleRef}
        >
          {title}
        </Typography>
      )}
      {row && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          pl={dreamcoatFrame ? 1 : 2}
          pb={1}
          sx={{
            position: 'sticky',
            top: `calc(env(safe-area-inset-top, ${inset.top + 'px'}) + 56px)`,
            bgcolor: 'background.default',
            zIndex: theme.zIndex?.appBar,
          }}
        >
          {row}
        </Stack>
      )}
    </>
  );
};
